import React, { useState } from 'react'
import { Typography, Checkbox, List, ListItem } from '@mui/material'
import { v4 as uuidv4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import { patchUserCalendarEvent } from 'services/userConnectionsHelper'
import RowStack from 'components/atoms/container/RowStack'
import {
  SummaryHead,
  InsightsNotification,
  ErrorNotification
} from './SummaryMolecules'
import { ColStack } from 'components/atoms/container/InCardStack'
import colors from 'styles/colors'
import { IconButtonWithTooltip } from 'components/atoms/button/StyledButton'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt'
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt'
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt'

export const RenderSummary = ({ event, isPreviousMeeting = false }) => {
  const { t } = useTranslation()
  const [isLiked, setIsLiked] = useState(event?.summary?.summaryLiked)
  const [actionItems, setActionItems] = useState(
    event?.summary?.assignedActionItems || {}
  )

  const handleActions = async (key, index, check) => {
    const newActionItems = { ...actionItems }
    newActionItems[key][index].checked = check
    setActionItems(newActionItems)
    await patchUserCalendarEvent(event.id, {
      summaryAssignedActionItems: JSON.stringify(newActionItems)
    })
  }

  const handleLikeDislike = async summaryLiked => {
    event.summary.summaryLiked = summaryLiked
    setIsLiked(summaryLiked)
    await patchUserCalendarEvent(event.id, {
      summaryLiked
    })
  }

  return (
    <>
      {event?.summary?.shortSummary ? (
        <ColStack spacing={8}>
          <ColStack spacing={4}>
            {!isPreviousMeeting && (
              <>
                <SummaryHead event={event} isSummary={true} />
                <Typography variant='h5'>{t('myTeam.TLDR')}</Typography>
              </>
            )}
            <Typography variant='body1KindaSmall' color={'primary'}>
              {event?.summary?.shortSummary}
            </Typography>
          </ColStack>

          {Object.keys(actionItems).length > 0 && (
            <ColStack spacing={4}>
              <Typography variant='h5'>{t('myTeam.actionItems')}</Typography>
              <ColStack spacing={8}>
                {Object.keys(actionItems).map(key => (
                  <ColStack key={uuidv4()} spacing={4}>
                    <Typography variant='body1KindaSmall' color={'primary'}>
                      {key}
                    </Typography>
                    {actionItems[key].map((action, index) => (
                      <RowStack
                        key={uuidv4()}
                        spacing={4}
                        alignItems={'flex-start'}>
                        <Checkbox
                          checked={action.checked}
                          onChange={e =>
                            handleActions(key, index, e.target.checked)
                          }
                          sx={{
                            padding: 1,
                            '&:not(.Mui-checked)': {
                              '& .MuiSvgIcon-root': {
                                color: colors.Grey350
                              }
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: 18
                            }
                          }}
                        />
                        <Typography
                          variant='body1KindaSmall'
                          color={'primary'}
                          lineHeight={'140%'}>
                          {action.text}
                        </Typography>
                      </RowStack>
                    ))}
                  </ColStack>
                ))}
              </ColStack>
            </ColStack>
          )}

          <RowStack spacing={8}>
            <IconButtonWithTooltip
              title={t('myTeam.goodSummary')}
              icon={isLiked ? <ThumbUpAltIcon /> : <ThumbUpOffAltIcon />}
              iconContainerProps={{ size: 24 }}
              onClick={() => handleLikeDislike(true)}
            />
            <IconButtonWithTooltip
              title={t('myTeam.badSummary')}
              icon={
                isLiked === false ? (
                  <ThumbDownAltIcon />
                ) : (
                  <ThumbDownOffAltIcon />
                )
              }
              iconContainerProps={{ size: 24 }}
              onClick={() => handleLikeDislike(false)}
            />
          </RowStack>
        </ColStack>
      ) : (
        <ErrorNotification
          title={t('myTeam.meetingSummaryError')}
          description={t('myTeam.errorGeneratingSummary')}
        />
      )}
    </>
  )
}

export const RenderNotes = ({ event }) => {
  const { t } = useTranslation()
  const detailNotes = event?.summary?.detailNotes
  return (
    <>
      {detailNotes ? (
        <ColStack spacing={8}>
          <SummaryHead event={event} />

          {Object.keys(detailNotes).length > 0 && (
            <>
              {Object.keys(detailNotes).map(key => (
                <ColStack key={uuidv4()} spacing={4}>
                  <Typography variant='h5' noWrap>
                    {key}
                  </Typography>

                  <List
                    sx={{ listStyleType: 'disc', padding: 0, paddingLeft: 11 }}>
                    {detailNotes[key].map(note => (
                      <ListItem
                        key={uuidv4()}
                        sx={{ display: 'list-item', padding: 0 }}>
                        <Typography variant='body1KindaSmall' color={'primary'}>
                          {note}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </ColStack>
              ))}
            </>
          )}
        </ColStack>
      ) : (
        <ErrorNotification
          title={t('myTeam.notesError')}
          description={t('myTeam.errorGeneratingNotes')}
        />
      )}
    </>
  )
}

export const RenderInsights = ({ event }) => {
  const { t } = useTranslation()
  const textProp = {
    variant: 'body1KindaSmall',
    color: 'primary',
    lineHeight: '140%'
  }

  const RenderTextBlock = ({ title, text }) => (
    <ColStack spacing={4}>
      <Typography variant='h5'>{title}</Typography>
      <Typography {...textProp}>{text}</Typography>
    </ColStack>
  )
  return (
    <>
      <ColStack
        spacing={4}
        sx={{
          // Remove it after insights are implemented
          opacity: 0.5,
          filter: 'blur(3px)'
        }}>
        <SummaryHead event={event} />
        <Typography variant='h5'>{t('overview.insights')}</Typography>
        <ColStack spacing={8}>
          <RenderTextBlock
            title='Header'
            text='Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Donec odio. Quisque volutpat mattis eros. Nullam malesuada erat ut turpis. Suspendisse urna nibh viverra non semper suscipit posuere a pede.'
          />
          <RenderTextBlock
            title='Start with rapport'
            text='Take a few minutes at the start of your meetings to ask Rachel how they’re doing. Building rapport before diving into work strengthens your connection and creates a more supportive environment.'
          />
          <RenderTextBlock
            title='Encouraging growth'
            text='Try asking your direct reports how they’re progressing on the goals they’ve set for themselves. It opens up a supportive conversation and helps identify where they may need more guidance or resources.'
          />
        </ColStack>
      </ColStack>
      <InsightsNotification />
    </>
  )
}
